import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ProjectsSectionProps {
  className?: string;
}

interface Project {
  title: string;
  description: string;
  longDescription: string;
  technologies: string[];
  category: string;
  status: 'Completed' | 'In Progress' | 'Deployed';
  highlights: string[];
  metrics?: {
    label: string;
    value: string;
  }[];
  demoUrl?: string;
  githubUrl?: string;
}

const ProjectsSection: React.FC<ProjectsSectionProps> = ({ className }) => {
  const projects: Project[] = [
    {
      title: 'AI-Powered Data Analytics Platform',
      description:
        'Enterprise-scale analytics platform processing billions of data points with real-time insights and machine learning predictions.',
      longDescription:
        'Built a comprehensive data analytics platform that processes massive datasets from multiple sources, providing real-time insights and predictive analytics for business decision-making.',
      technologies: [
        'Python',
        'TensorFlow',
        'Apache Airflow',
        'PostgreSQL',
        'React.js',
        'TypeScript',
        'AWS',
        'Docker',
      ],
      category: 'AI/Machine Learning',
      status: 'Deployed',
      highlights: [
        'Processes 10+ billion data points daily with 99.9% uptime',
        'Reduced data processing time by 75% through optimized algorithms',
        'Implemented real-time ML predictions with <100ms latency',
        'Designed scalable microservices architecture supporting 1000+ concurrent users',
      ],
      metrics: [
        { label: 'Data Points', value: '10B+' },
        { label: 'Performance Gain', value: '75%' },
        { label: 'Uptime', value: '99.9%' },
        { label: 'Users', value: '1000+' },
      ],
      demoUrl: 'https://demo.analytics-platform.com',
      githubUrl: 'https://github.com/yourusername/ai-analytics-platform',
    },
    {
      title: 'Microservices E-Commerce Platform',
      description:
        'Scalable e-commerce platform built with microservices architecture, handling millions of transactions with advanced payment processing.',
      longDescription:
        'Developed a modern e-commerce platform using microservices architecture, featuring advanced payment processing, inventory management, and real-time order tracking.',
      technologies: [
        'Java',
        'Spring Boot',
        'Kotlin',
        'GraphQL',
        'DynamoDB',
        'React.js',
        'TypeScript',
        'AWS Lambda',
        'Kubernetes',
      ],
      category: 'Full-Stack Development',
      status: 'Deployed',
      highlights: [
        'Handles 1M+ transactions per day with zero downtime',
        'Implemented advanced payment processing with multiple gateways',
        'Built real-time inventory management across 50+ warehouses',
        'Achieved 40% faster page load times through optimization',
      ],
      metrics: [
        { label: 'Transactions/Day', value: '1M+' },
        { label: 'Warehouses', value: '50+' },
        { label: 'Load Time Improvement', value: '40%' },
        { label: 'Downtime', value: '0%' },
      ],
      demoUrl: 'https://demo.ecommerce-platform.com',
      githubUrl: 'https://github.com/yourusername/microservices-ecommerce',
    },
    {
      title: 'Real-Time Collaboration Tool',
      description:
        'Modern collaboration platform with real-time editing, video conferencing, and AI-powered meeting insights for distributed teams.',
      longDescription:
        'Created a comprehensive collaboration platform that enables distributed teams to work together seamlessly with real-time document editing, integrated video conferencing, and AI-powered meeting analytics.',
      technologies: [
        'React.js',
        'TypeScript',
        'Node.js',
        'Socket.io',
        'MongoDB',
        'WebRTC',
        'Python',
        'TensorFlow',
        'AWS',
      ],
      category: 'Real-Time Systems',
      status: 'Deployed',
      highlights: [
        'Supports 10,000+ concurrent users with real-time synchronization',
        'Integrated WebRTC for seamless video conferencing',
        'AI-powered meeting transcription and insights generation',
        'Built collaborative document editing with conflict resolution',
      ],
      metrics: [
        { label: 'Concurrent Users', value: '10K+' },
        { label: 'Meeting Accuracy', value: '95%' },
        { label: 'Sync Latency', value: '<50ms' },
        { label: 'User Satisfaction', value: '4.8/5' },
      ],
      demoUrl: 'https://demo.collaboration-tool.com',
      githubUrl: 'https://github.com/yourusername/realtime-collaboration',
    },
    {
      title: 'DevOps Automation Pipeline',
      description:
        'Comprehensive CI/CD pipeline with automated testing, deployment, and monitoring for enterprise applications.',
      longDescription:
        'Designed and implemented a complete DevOps automation pipeline that streamlines the software development lifecycle with automated testing, deployment, and comprehensive monitoring.',
      technologies: [
        'Docker',
        'Kubernetes',
        'Jenkins',
        'Terraform',
        'AWS',
        'Prometheus',
        'Grafana',
        'Python',
        'Bash',
      ],
      category: 'DevOps/Infrastructure',
      status: 'Deployed',
      highlights: [
        'Reduced deployment time from 4 hours to 15 minutes',
        'Achieved 99.9% deployment success rate with automated rollbacks',
        'Implemented comprehensive monitoring with 50+ custom metrics',
        'Automated testing pipeline with 95% code coverage enforcement',
      ],
      metrics: [
        { label: 'Deployment Time', value: '15min' },
        { label: 'Success Rate', value: '99.9%' },
        { label: 'Code Coverage', value: '95%' },
        { label: 'Monitoring Metrics', value: '50+' },
      ],
      githubUrl: 'https://github.com/yourusername/devops-automation',
    },
    {
      title: 'Blockchain Supply Chain Tracker',
      description:
        'Blockchain-based supply chain tracking system ensuring transparency and authenticity across global logistics networks.',
      longDescription:
        'Developed a blockchain-powered supply chain tracking system that provides end-to-end transparency and authenticity verification for global logistics operations.',
      technologies: [
        'Solidity',
        'Ethereum',
        'Web3.js',
        'React.js',
        'TypeScript',
        'Node.js',
        'IPFS',
        'PostgreSQL',
      ],
      category: 'Blockchain/Web3',
      status: 'In Progress',
      highlights: [
        'Tracks 100,000+ products across global supply chains',
        'Immutable record keeping with smart contract automation',
        'Integration with IoT sensors for real-time tracking',
        'Reduced fraud detection time by 90% through blockchain verification',
      ],
      metrics: [
        { label: 'Products Tracked', value: '100K+' },
        { label: 'Fraud Reduction', value: '90%' },
        { label: 'Network Nodes', value: '500+' },
        { label: 'Transaction Speed', value: '<5s' },
      ],
      githubUrl: 'https://github.com/yourusername/blockchain-supply-chain',
    },
    {
      title: 'Mobile Health Monitoring App',
      description:
        'Cross-platform mobile application for health monitoring with AI-powered insights and real-time data synchronization.',
      longDescription:
        'Built a comprehensive mobile health monitoring application that integrates with wearable devices, provides AI-powered health insights, and enables seamless data sharing with healthcare providers.',
      technologies: [
        'React Native',
        'TypeScript',
        'Python',
        'TensorFlow',
        'Firebase',
        'HealthKit',
        'Google Fit',
        'AWS',
      ],
      category: 'Mobile Development',
      status: 'Completed',
      highlights: [
        'Integrates with 20+ wearable device types and health sensors',
        'AI-powered health trend analysis and personalized recommendations',
        'HIPAA-compliant data handling and secure cloud synchronization',
        'Real-time health alerts and emergency contact notifications',
      ],
      metrics: [
        { label: 'Device Integrations', value: '20+' },
        { label: 'Active Users', value: '50K+' },
        { label: 'Data Accuracy', value: '98%' },
        { label: 'App Rating', value: '4.7/5' },
      ],
      demoUrl: 'https://demo.health-monitor-app.com',
      githubUrl: 'https://github.com/yourusername/mobile-health-monitor',
    },
  ];

  const categories = [
    'All',
    'AI/Machine Learning',
    'Full-Stack Development',
    'Real-Time Systems',
    'DevOps/Infrastructure',
    'Blockchain/Web3',
    'Mobile Development',
  ];
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const filteredProjects =
    selectedCategory === 'All'
      ? projects
      : projects.filter(project => project.category === selectedCategory);

  return (
    <section id='projects' className={cn('py-20 bg-background', className)}>
      <div className='container mx-auto px-4'>
        {/* Section Header */}
        <div className='text-center mb-16'>
          <h2 className='text-3xl sm:text-4xl font-bold text-foreground mb-4'>
            Featured Projects
          </h2>
          <p className='text-lg text-muted-foreground max-w-3xl mx-auto mb-8'>
            A showcase of innovative projects demonstrating expertise in AI/ML,
            full-stack development, DevOps automation, and emerging
            technologies. Each project reflects a commitment to scalable
            solutions and technical excellence.
          </p>

          {/* Category Filter */}
          <div className='flex flex-wrap justify-center gap-2 mb-8'>
            {categories.map(category => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size='sm'
                onClick={() => setSelectedCategory(category)}
                className='rounded-full'
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16'>
          {filteredProjects.map((project, index) => (
            <Card
              key={index}
              className='hover:shadow-lg transition-shadow duration-300 h-full flex flex-col'
            >
              <CardHeader className='pb-4'>
                <div className='flex items-start justify-between mb-4'>
                  <div className='flex-1'>
                    <CardTitle className='text-xl text-primary mb-2'>
                      {project.title}
                    </CardTitle>
                    <p className='text-sm text-muted-foreground mb-3'>
                      {project.description}
                    </p>
                    <div className='flex items-center gap-2 mb-3'>
                      <Badge variant='outline' className='text-xs'>
                        {project.category}
                      </Badge>
                      <Badge variant='outline' className={cn('text-xs border')}>
                        {project.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className='flex-1 flex flex-col space-y-6'>
                {/* Project Details */}
                <div>
                  <p className='text-sm text-muted-foreground leading-relaxed'>
                    {project.longDescription}
                  </p>
                </div>

                {/* Key Highlights */}
                <div>
                  <h4 className='text-sm font-semibold text-foreground mb-3'>
                    Key Highlights
                  </h4>
                  <ul className='space-y-2'>
                    {project.highlights.map((highlight, highlightIndex) => (
                      <li
                        key={highlightIndex}
                        className='flex items-start gap-2'
                      >
                        <span className='text-primary mt-1.5 text-xs'>▸</span>
                        <span className='text-sm text-muted-foreground leading-relaxed'>
                          {highlight}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Metrics */}
                {project.metrics && (
                  <div>
                    <h4 className='text-sm font-semibold text-foreground mb-3'>
                      Project Metrics
                    </h4>
                    <div className='grid grid-cols-2 gap-3'>
                      {project.metrics.map((metric, metricIndex) => (
                        <div
                          key={metricIndex}
                          className='text-center p-3 bg-muted/30 rounded-lg'
                        >
                          <div className='text-lg font-bold text-primary'>
                            {metric.value}
                          </div>
                          <div className='text-xs text-muted-foreground'>
                            {metric.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Technologies */}
                <div>
                  <h4 className='text-sm font-semibold text-foreground mb-3'>
                    Technologies Used
                  </h4>
                  <div className='flex flex-wrap gap-2'>
                    {project.technologies.map(tech => (
                      <Badge key={tech} variant='secondary' className='text-xs'>
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Projects Summary */}
        <div className='text-center'>
          <h3 className='text-2xl font-semibold text-foreground mb-6'>
            Project Portfolio Summary
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-6 max-w-4xl mx-auto'>
            <div className='text-center'>
              <div className='text-3xl font-bold text-primary mb-2'>
                {projects.length}
              </div>
              <div className='text-sm text-muted-foreground'>
                Projects Completed
              </div>
            </div>
            <div className='text-center'>
              <div className='text-3xl font-bold text-primary mb-2'>
                {categories.length - 1}
              </div>
              <div className='text-sm text-muted-foreground'>
                Technology Categories
              </div>
            </div>
            <div className='text-center'>
              <div className='text-3xl font-bold text-primary mb-2'>100%</div>
              <div className='text-sm text-muted-foreground'>
                Production Ready
              </div>
            </div>
            <div className='text-center'>
              <div className='text-3xl font-bold text-primary mb-2'>24/7</div>
              <div className='text-sm text-muted-foreground'>Uptime Focus</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
